import { Alert, Autocomplete, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCommonApi } from '../api';
import { onSuccessCustomApiResponse } from '../api/utils';
import { formatPrice } from '../utils/formatPrice';
import { PageLayout } from './common';
import { CancelConfirmDialog } from './sourceCommissions/CancelConfirmDialog';
import { ConfirmCreateDialog } from './sourceCommissions/ConfirmCreateDialog';
import { CreateResultDialog } from './sourceCommissions/CreateResultDialog';
import { PreviewDialog } from './sourceCommissions/PreviewDialog';
import { SourceNameCell } from './sourceCommissions/SourceNameCell';
import {
  CancelSourceCommissionsResult,
  CreateSourceCommissionDocumentsResult,
  SourceCommission,
  SourceCommissionPreviewResult
} from './sourceCommissions/type/sourceCommissions';
import {
  buildCancelBody,
  buildCreateBody,
  estimateDocuments,
  getAccountingYears,
  getPaymentTypeLabel,
  getSelectionSignature,
  isValidAmount,
  sumAmounts
} from './sourceCommissions/utils';

const API_DATE_FORMAT = 'dd.MM.yyyy';
const CURRENT_YEAR = new Date().getFullYear();
const ACCOUNTING_YEARS = getAccountingYears(CURRENT_YEAR);

interface SourceOption {
  id: number;
  name: string;
}

const parseAmountInput = (value: string) => Number.parseFloat(value.replace(/\s/g, '').replace(',', '.'));

export const UnpaidCommissionsPage = () => {
  const api = useCommonApi();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [source, setSource] = useState<SourceOption | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [knownSources, setKnownSources] = useState<Record<number, string>>({});
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [amountInputs, setAmountInputs] = useState<Record<number, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [issueDate, setIssueDate] = useState(new Date());
  const [result, setResult] = useState<CreateSourceCommissionDocumentsResult>();
  const [preview, setPreview] = useState<{ signature: string; result: SourceCommissionPreviewResult }>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelFailed, setCancelFailed] = useState<number[]>([]);
  const previewRequestId = useRef(0);

  const filter = useMemo(
    () => ({
      year,
      sourceId: source?.id,
      dateFrom: dateFrom ? format(dateFrom, API_DATE_FORMAT) : undefined,
      dateTo: dateTo ? format(dateTo, API_DATE_FORMAT) : undefined
    }),
    [year, source, dateFrom, dateTo]
  );

  const { data: rows, isPending, isError, refetch } = useQuery(api.getSourceCommissions(filter));
  const { mutate: createDocuments, isPending: isCreating } = useMutation(api.createSourceCommissionDocuments());
  const { mutate: previewDocuments, isPending: isPreviewing } = useMutation(api.previewSourceCommissionDocuments());
  const { mutate: cancelCommissions, isPending: isCancelling } = useMutation(api.cancelSourceCommissions());

  useEffect(() => {
    if (!rows) return;
    setKnownSources((prev) => ({ ...prev, ...Object.fromEntries(rows.map((r) => [r.sourceId, r.sourceName])) }));
    const ids = new Set(rows.map((r) => r.accidentId));
    setSelectedIds((prev) => prev.filter((id) => ids.has(id)));
  }, [rows]);

  const sourceOptions = useMemo<SourceOption[]>(
    () => Object.entries(knownSources).map(([id, name]) => ({ id: Number(id), name })),
    [knownSources]
  );

  const selectedRows = useMemo(
    () => (rows ?? []).filter((r) => selectedIds.includes(r.accidentId)),
    [rows, selectedIds]
  );

  const effectiveAmount = (row: SourceCommission) => {
    const input = amountInputs[row.accidentId];
    return input === undefined ? row.amount : parseAmountInput(input);
  };
  const effectiveRows = selectedRows.map((row) => ({ ...row, amount: effectiveAmount(row) }));
  const hasInvalidAmount = effectiveRows.some((r) => !isValidAmount(r.amount));
  const total = sumAmounts(effectiveRows);
  const issueDateText = format(issueDate, API_DATE_FORMAT);
  const signature = getSelectionSignature(effectiveRows, issueDateText);
  const isPreviewCurrent = preview?.signature === signature;

  // Změna výběru/částek/data zneplatní náhled i rozpracovaný požadavek na náhled.
  useEffect(() => {
    previewRequestId.current += 1;
  }, [signature]);

  const columns = useMemo<GridColDef<SourceCommission>[]>(
    () => [
      { field: 'contractNumber', headerName: 'Nehoda', flex: 1, minWidth: 120 },
      { field: 'date', headerName: 'Datum', width: 110 },
      {
        field: 'sourceName',
        headerName: 'Tipař',
        flex: 1,
        minWidth: 170,
        renderCell: ({ row }) => <SourceNameCell name={row.sourceName} tradeName={row.sourceTradeName} />
      },
      { field: 'ownerName', headerName: 'Majitel vozidla', flex: 1, minWidth: 150 },
      { field: 'invoiceNumbers', headerName: 'Faktura', flex: 1, minWidth: 120 },
      {
        field: 'commissionValue',
        headerName: 'Provize',
        width: 170,
        renderCell: ({ row }) => {
          const paymentLabel = getPaymentTypeLabel(row.paymentType);
          return (
            <Stack justifyContent="center" height={1} lineHeight={1.3}>
              <Typography variant="body2">{row.commissionValue}</Typography>
              {paymentLabel && (
                <Typography variant="caption" color="text.secondary">
                  {paymentLabel}
                </Typography>
              )}
            </Stack>
          );
        }
      },
      { field: 'year', headerName: 'Účetní rok', width: 100 },
      {
        field: 'amount',
        headerName: 'Částka',
        width: 170,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderCell: ({ row }) => {
          if (!selectedIds.includes(row.accidentId)) return formatPrice(row.amount, 2);
          const value = amountInputs[row.accidentId] ?? String(row.amount);
          return (
            <TextField
              size="small"
              value={value}
              error={!isValidAmount(parseAmountInput(value))}
              onChange={(e) => setAmountInputs((prev) => ({ ...prev, [row.accidentId]: e.target.value }))}
              onKeyDown={(e) => e.stopPropagation()}
              slotProps={{ htmlInput: { inputMode: 'decimal', 'aria-label': 'Částka' } }}
              sx={{ width: 140 }}
            />
          );
        }
      }
    ],
    [selectedIds, amountInputs]
  );

  const onPreview = () => {
    const requestId = ++previewRequestId.current;
    const requestSignature = signature;
    const overrides = Object.fromEntries(effectiveRows.map((r) => [r.accidentId, r.amount]));
    previewDocuments(buildCreateBody(selectedRows, overrides, issueDateText), {
      onSuccess: onSuccessCustomApiResponse({
        executed: (_, data: SourceCommissionPreviewResult) => {
          if (requestId !== previewRequestId.current) return; // zastaralá odpověď
          setPreview({ signature: requestSignature, result: data });
          setPreviewOpen(true);
        }
      })
    });
  };

  const onCancelConfirm = () => {
    cancelCommissions(buildCancelBody(selectedRows), {
      onSuccess: onSuccessCustomApiResponse({
        executed: (_, data: CancelSourceCommissionsResult) => {
          setCancelOpen(false);
          setCancelFailed(data.failed ?? []);
          setSelectedIds([]);
          setAmountInputs({});
          setPreview(undefined);
        },
        failed: () => setCancelOpen(false)
      }),
      onError: () => setCancelOpen(false)
    });
  };

  const onConfirm = () => {
    const overrides = Object.fromEntries(effectiveRows.map((r) => [r.accidentId, r.amount]));
    createDocuments(buildCreateBody(selectedRows, overrides, issueDateText), {
      onSuccess: onSuccessCustomApiResponse({
        executed: (_, data: CreateSourceCommissionDocumentsResult) => {
          setConfirmOpen(false);
          setPreview(undefined);
          setResult(data);
          setSelectedIds([]);
          setAmountInputs({});
        },
        failed: () => {
          setConfirmOpen(false);
          void refetch();
        }
      }),
      onError: () => {
        setConfirmOpen(false);
        void refetch();
      }
    });
  };

  return (
    <PageLayout title="POJ k vyplacení">
      <Stack gap={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ backgroundColor: 'background.paper', p: 2 }}>
          <TextField
            select
            label="Účetní rok"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            sx={{ minWidth: 140 }}
          >
            {ACCOUNTING_YEARS.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            sx={{ minWidth: 260 }}
            options={sourceOptions}
            value={source}
            onChange={(_, value) => setSource(value)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="Žádný tipař"
            renderInput={(params) => <TextField {...params} label="Tipař" />}
          />
          <DatePicker
            label="Datum nehody od"
            format={API_DATE_FORMAT}
            value={dateFrom}
            onChange={setDateFrom}
            slotProps={{ field: { clearable: true } }}
          />
          <DatePicker
            label="Datum nehody do"
            format={API_DATE_FORMAT}
            value={dateTo}
            onChange={setDateTo}
            slotProps={{ field: { clearable: true } }}
          />
        </Stack>

        <Alert severity="info">
          POJ v procentech se v seznamu objeví až po úplném zaplacení faktury (celé částky, ne jen DPH). POJ v Kč se
          zobrazuje vždy. Pokud nějaká procentní POJ zatím chybí, neznamená to chybu.
        </Alert>

        {cancelFailed.length > 0 && (
          <Alert severity="warning" onClose={() => setCancelFailed([])}>
            Tyto POJ se nepodařilo označit jako nevyplácet (už jsou vyplacené, na dokladu nebo zrušené):{' '}
            {cancelFailed.join(', ')}.
          </Alert>
        )}

        {isError && (
          <Alert severity="error" action={<Button onClick={() => refetch()}>Zkusit znovu</Button>}>
            Nepodařilo se načíst seznam POJ.
          </Alert>
        )}

        <Box sx={{ backgroundColor: 'background.paper', p: 1, overflowX: 'auto' }}>
          <DataGrid
            autoHeight
            rows={rows ?? []}
            columns={columns}
            loading={isPending}
            getRowId={(row: SourceCommission) => row.accidentId}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(model: GridRowSelectionModel) => setSelectedIds(model.map(Number))}
            localeText={{ noRowsLabel: 'Žádné POJ k vyplacení', footerRowSelected: (n) => `Vybráno: ${n}` }}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </Box>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          gap={2}
          sx={{ backgroundColor: 'background.paper', p: 2 }}
        >
          <Typography>
            Vybráno: {selectedRows.length} · Součet: <b>{formatPrice(total, 2)}</b>
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }}>
            {selectedRows.length > 0 && !hasInvalidAmount && !isPreviewCurrent && (
              <Typography variant="body2" color="text.secondary">
                Před vystavením zkontrolujte náhled.
              </Typography>
            )}
            <Button
              color="error"
              variant="outlined"
              disabled={selectedRows.length === 0}
              onClick={() => setCancelOpen(true)}
            >
              Nevyplácet
            </Button>
            <Button
              variant="outlined"
              disabled={selectedRows.length === 0 || hasInvalidAmount}
              loading={isPreviewing}
              onClick={onPreview}
            >
              Generovat
            </Button>
            <Button
              variant="contained"
              disabled={selectedRows.length === 0 || hasInvalidAmount || !isPreviewCurrent}
              onClick={() => setConfirmOpen(true)}
            >
              Vystavit
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <ConfirmCreateDialog
        open={confirmOpen}
        documentsCount={estimateDocuments(selectedRows)}
        itemsCount={selectedRows.length}
        total={total}
        issueDate={issueDate}
        onIssueDateChange={setIssueDate}
        isLoading={isCreating}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onConfirm}
      />
      <PreviewDialog open={previewOpen} result={preview?.result} onClose={() => setPreviewOpen(false)} />
      <CancelConfirmDialog
        open={cancelOpen}
        itemsCount={selectedRows.length}
        isLoading={isCancelling}
        onClose={() => setCancelOpen(false)}
        onConfirm={onCancelConfirm}
      />
      <CreateResultDialog result={result} onClose={() => setResult(undefined)} />
    </PageLayout>
  );
};
